import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, Lock, Sparkles, Tag, X, Loader2,
  ShieldCheck, CreditCard, RefreshCw,
  Download, Calendar, ChefHat, ShoppingCart,
} from 'lucide-react';
import { SEOMeta } from '@/components/SEOMeta';
import { useAuth } from '@/contexts/AuthContext';
import { useAppSettings } from '@/hooks/useAppSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const INCLUDED = [
  { icon: Calendar,     label: '7-дневен персонализиран план',  sub: 'Понеделник — неделя' },
  { icon: ShoppingCart, label: 'Списък за пазаруване',          sub: '3 бюджетни варианта' },
  { icon: ChefHat,      label: 'Пълни рецепти с приготвяне',    sub: 'Стъпка по стъпка' },
  { icon: Download,     label: 'PDF изтегляне',                  sub: 'Принтирай плана' },
  { icon: RefreshCw,    label: 'Нови планове по всяко време',   sub: 'Неограничено' },
];

export default function CheckoutPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { subscription_price_eur: BASE_PRICE } = useAppSettings();

  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null); // { valid, discount_percent, label, new_price }
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // If user is already subscribed, redirect to wallet
  useEffect(() => {
    if (user && user.subscription_status === 'active') {
      navigate('/wallet');
    }
  }, [user, navigate]);

  // If not logged in, redirect to register
  useEffect(() => {
    if (user === null) {
      navigate('/register?redirect=checkout');
    }
  }, [user, navigate]);

  const finalPrice = promoResult?.valid ? promoResult.new_price : BASE_PRICE;
  const discountAmount = promoResult?.valid ? (BASE_PRICE - promoResult.new_price).toFixed(2) : null;

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const res = await axios.post(`${API}/promo/validate`, { code: promoCode.trim() });
      setPromoResult(res.data);
      toast.success(`Промо кодът е приложен — ${res.data.label}`);
    } catch (e) {
      setPromoError(e?.response?.data?.detail || 'Невалиден промо код');
      setPromoResult(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const clearPromo = () => {
    setPromoCode('');
    setPromoResult(null);
    setPromoError('');
  };

  const handleCheckout = async () => {
    if (!user) { navigate('/login?redirect=checkout'); return; }
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        origin_url: window.location.origin,
        plan_id: '',
        success_path: '/wallet',
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Грешка при стартиране на плащането');
      setCheckoutLoading(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <SEOMeta
        title="Плащане"
        description={`Абонирай се за portion.bg за €${Number(BASE_PRICE).toFixed(2)}/месец.`}
        canonical="/checkout"
        noindex={true}
      />

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            {/* Page header */}
            <div className="text-center mb-10">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Завърши абонамента</h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Един абонамент. Пълен достъп. Отмени по всяко време.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

              {/* ── Left: Included features ── */}
              <div className="lg:col-span-5 space-y-4">
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-gray-100 dark:border-slate-700"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.05)' }}>
                  <div className="flex items-center gap-2 mb-5">
                    <Sparkles className="w-4 h-4 text-emerald-500" />
                    <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide">Включено в абонамента</h2>
                  </div>
                  <div className="space-y-3.5">
                    {INCLUDED.map(({ icon: Icon, label, sub }) => (
                      <div key={label} className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-tight">{label}</p>
                          <p className="text-xs text-slate-400">{sub}</p>
                        </div>
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Trust badges */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { icon: ShieldCheck, label: 'SSL шифроване' },
                    { icon: Lock,        label: 'Stripe защита' },
                    { icon: RefreshCw,   label: 'Отмени лесно' },
                  ].map(({ icon: Icon, label }) => (
                    <div key={label} className="bg-white dark:bg-slate-800 rounded-xl p-3 text-center border border-gray-100 dark:border-slate-700"
                      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                      <Icon className="w-4 h-4 text-gray-400 mx-auto mb-1.5" />
                      <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 leading-tight">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Right: Order summary + Promo + Pay ── */}
              <div className="lg:col-span-7">
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.07)' }}>

                  {/* Dark header */}
                  <div className="bg-gray-900 px-6 py-5">
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-2">Обобщение на поръчката</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-base font-bold text-white">portion.bg — Месечен абонамент</p>
                        <p className="text-sm text-white/40">Пълен достъп до 7-дневния план</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-black text-white">{finalPrice.toFixed(2)} €</p>
                        <p className="text-xs text-white/30">/месец</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-5">
                    {/* Price breakdown */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400">Месечен абонамент</span>
                        <span className={`font-medium ${promoResult?.valid ? 'line-through text-slate-400' : 'text-gray-900 dark:text-gray-100'}`}>
                          {BASE_PRICE.toFixed(2)} €
                        </span>
                      </div>
                      {promoResult?.valid && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="flex justify-between text-sm"
                        >
                          <span className="text-emerald-600 font-medium flex items-center gap-1.5">
                            <Tag className="w-3.5 h-3.5" />
                            Промо код ({promoResult.code}) —{promoResult.discount_percent}%
                          </span>
                          <span className="font-semibold text-emerald-600">−{discountAmount} €</span>
                        </motion.div>
                      )}
                      <div className="border-t border-gray-100 dark:border-slate-700 pt-2 flex justify-between">
                        <span className="font-semibold text-gray-900 dark:text-gray-100">Общо днес</span>
                        <span className="text-lg font-black text-gray-900 dark:text-gray-100">{finalPrice.toFixed(2)} €</span>
                      </div>
                    </div>

                    {/* Promo code input */}
                    <div>
                      <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2 block">
                        Промо код (по избор)
                      </label>
                      {promoResult?.valid ? (
                        <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-emerald-700">{promoResult.code}</p>
                            <p className="text-xs text-emerald-600">{promoResult.label}</p>
                          </div>
                          <button onClick={clearPromo} className="text-emerald-400 hover:text-emerald-600 transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={promoCode}
                              onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                              onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
                              placeholder="Напр. WELCOME20"
                              data-testid="promo-code-input"
                              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300"
                            />
                          </div>
                          <button
                            onClick={handleApplyPromo}
                            disabled={promoLoading || !promoCode.trim()}
                            data-testid="promo-code-apply"
                            className="px-4 py-2.5 bg-gray-900 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors flex items-center gap-1.5"
                          >
                            {promoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                            Приложи
                          </button>
                        </div>
                      )}
                      {promoError && (
                        <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                          <X className="w-3 h-3" /> {promoError}
                        </p>
                      )}
                    </div>

                    {/* Account info */}
                    <div className="flex items-center gap-3 bg-gray-50 dark:bg-slate-900 rounded-xl px-4 py-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">
                          {user?.full_name?.[0] || user?.email?.[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-400 leading-none mb-0.5">Акаунт</p>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{user?.email}</p>
                      </div>
                    </div>

                    {/* Pay button */}
                    <button
                      onClick={handleCheckout}
                      disabled={checkoutLoading}
                      data-testid="checkout-pay-button"
                      className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{
                        background: '#059669',
                        boxShadow: '0 0 24px rgba(5,150,105,0.25)',
                      }}
                    >
                      {checkoutLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Пренасочване към Stripe...</>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Плати {finalPrice.toFixed(2)} € / месец
                        </>
                      )}
                    </button>

                    {/* Trust line */}
                    <div className="flex items-center justify-center gap-2">
                      <Lock className="w-3.5 h-3.5 text-gray-400" />
                      <p className="text-xs text-gray-400">Сигурно плащане чрез Stripe. Без скрити такси.</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
